import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import "./Calendar.css";

import {
  FaCalendarAlt,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaBell,
  FaPlaneDeparture,
  FaHandshake,
  FaUserClock,
  FaTasks,
  FaFileContract,
  FaDollarSign,
  FaTimes,
  FaExclamationTriangle,
  FaSyncAlt,
} from "react-icons/fa";

import CalendarMonthView from "./CalendarMonthView";
import CalendarWeekView from "./CalendarWeekView";
import CalendarAgendaView from "./CalendarAgendaView";
import CalendarEventModal from "./CalendarEventModal";
import CalendarEventDetailModal from "./CalendarEventDetailModal";

const CalendarPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialViewParam = searchParams.get("view");

  const [currentView, setCurrentView] = useState(
    initialViewParam === "reminders" || initialViewParam === "agenda"
      ? "agenda"
      : "month"
  );

  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    total_events: 0,
    meetings_count: 0,
    followups_count: 0,
    flights_count: 0,
    tasks_count: 0,
    expiries_count: 0,
    today_count: 0,
    upcoming_48h_count: 0,
    overdue_count: 0,
  });
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedEventForModal, setSelectedEventForModal] = useState(null);
  const [selectedInitialDate, setSelectedInitialDate] = useState(null);
  const [detailModalEvent, setDetailModalEvent] = useState(null);

  // Fetch KPI Stats
  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/calendar/stats`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch calendar stats:", err);
    }
  };

  // Fetch Reminders
  const fetchReminders = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/calendar/reminders`);
      setReminders(res.data);
    } catch (err) {
      console.error("Failed to fetch reminders:", err);
    }
  };

  // Fetch Events with active filters
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (moduleFilter) params.module_source = moduleFilter;
      if (eventTypeFilter) params.event_type = eventTypeFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const res = await axios.get(`${API_BASE_URL}/calendar/events`, { params });
      setEvents(res.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch calendar events:", err);
      setLoading(false);
    }
  }, [moduleFilter, eventTypeFilter, searchQuery]);

  useEffect(() => {
    fetchStats();
    fetchReminders();
    fetchEvents();
  }, [fetchEvents]);

  // Navigate Date Period
  const handlePrev = () => {
    const next = new Date(currentDate);
    if (currentView === "month") {
      next.setMonth(next.getMonth() - 1);
    } else if (currentView === "week") {
      next.setDate(next.getDate() - 7);
    } else {
      next.setDate(next.getDate() - 1);
    }
    setCurrentDate(next);
  };

  const handleNext = () => {
    const next = new Date(currentDate);
    if (currentView === "month") {
      next.setMonth(next.getMonth() + 1);
    } else if (currentView === "week") {
      next.setDate(next.getDate() + 7);
    } else {
      next.setDate(next.getDate() + 1);
    }
    setCurrentDate(next);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const formatPeriodLabel = () => {
    if (currentView === "month") {
      return currentDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    }
    if (currentView === "week") {
      const curr = new Date(currentDate);
      const first = curr.getDate() - curr.getDay();
      const start = new Date(curr.setDate(first));
      const end = new Date(curr.setDate(first + 6));
      return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} - ${end.toLocaleDateString(
        undefined,
        { month: "short", day: "numeric", year: "numeric" }
      )}`;
    }
    return currentDate.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Dismiss Reminder
  const handleDismissReminder = async (eventId) => {
    try {
      await axios.post(`${API_BASE_URL}/calendar/reminders/${eventId}/dismiss`);
      fetchReminders();
      fetchEvents();
      fetchStats();
    } catch (err) {
      console.error("Failed to dismiss reminder:", err);
    }
  };

  // Open Create Modal for specific date
  const handleAddEventForDate = (dateObj) => {
    setSelectedInitialDate(dateObj);
    setSelectedEventForModal(null);
    setIsCreateModalOpen(true);
  };

  // Filter events in view if we are on Reminders tab
  const displayEvents =
    initialViewParam === "reminders"
      ? events.filter(
          (e) =>
            e.priority === "urgent" ||
            e.priority === "high" ||
            e.event_type === "Reminder" ||
            reminders.some((r) => r.event_id === e.id)
        )
      : events;

  return (
    <div className="calendar-page-container">
      {/* Top Header Card */}
      <div className="calendar-header-card">
        <div className="calendar-title-group">
          <h1>
            <FaCalendarAlt style={{ color: "#2563eb" }} />
            Unified Business Calendar & Reminders
          </h1>
          <p>
            Real-time schedule of client meetings, follow-ups, flight bookings, task deadlines, and compliance expiries across all modules.
          </p>
        </div>

        <div className="calendar-header-actions">
          <button
            type="button"
            className="calendar-secondary-btn"
            onClick={() => {
              fetchEvents();
              fetchStats();
              fetchReminders();
            }}
            title="Refresh schedule"
          >
            <FaSyncAlt /> Refresh
          </button>
          <button
            type="button"
            className="calendar-primary-btn"
            onClick={() => {
              setSelectedInitialDate(new Date());
              setSelectedEventForModal(null);
              setIsCreateModalOpen(true);
            }}
          >
            <FaPlus /> Schedule Event / Meeting
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="calendar-kpi-grid">
        <div className="calendar-kpi-card">
          <div className="kpi-icon-wrapper" style={{ backgroundColor: "#eff6ff", color: "#2563eb" }}>
            <FaCalendarAlt />
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{stats.total_events}</span>
            <span className="kpi-label">Total Scheduled</span>
          </div>
        </div>

        <div className="calendar-kpi-card">
          <div className="kpi-icon-wrapper" style={{ backgroundColor: "#ede9fe", color: "#7c3aed" }}>
            <FaHandshake />
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{stats.meetings_count}</span>
            <span className="kpi-label">Client Meetings</span>
          </div>
        </div>

        <div className="calendar-kpi-card">
          <div className="kpi-icon-wrapper" style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}>
            <FaPlaneDeparture />
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{stats.flights_count}</span>
            <span className="kpi-label">Flight Bookings</span>
          </div>
        </div>

        <div className="calendar-kpi-card">
          <div className="kpi-icon-wrapper" style={{ backgroundColor: "#fef3c7", color: "#d97706" }}>
            <FaUserClock />
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{stats.followups_count}</span>
            <span className="kpi-label">Inquiry Follow-ups</span>
          </div>
        </div>

        <div className="calendar-kpi-card">
          <div className="kpi-icon-wrapper" style={{ backgroundColor: "#e0e7ff", color: "#4f46e5" }}>
            <FaTasks />
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{stats.tasks_count}</span>
            <span className="kpi-label">Task Deadlines</span>
          </div>
        </div>

        <div className="calendar-kpi-card">
          <div className="kpi-icon-wrapper" style={{ backgroundColor: "#ffe4e6", color: "#e11d48" }}>
            <FaFileContract />
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{stats.expiries_count}</span>
            <span className="kpi-label">Compliance Expiries</span>
          </div>
        </div>
      </div>

      {/* Reminders / Urgent Alerts Banner */}
      {reminders.length > 0 && (
        <div className={`calendar-alerts-banner ${stats.overdue_count > 0 ? "has-critical" : ""}`}>
          <div className="alerts-banner-top">
            <div className="alerts-banner-title">
              {stats.overdue_count > 0 ? (
                <FaExclamationTriangle style={{ color: "#e11d48" }} />
              ) : (
                <FaBell style={{ color: "#d97706" }} />
              )}
              <span>
                Active Reminders & Deadlines ({reminders.length} item{reminders.length > 1 ? "s" : ""})
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {stats.today_count} due today • {stats.overdue_count} overdue
            </div>
          </div>

          <div className="alerts-items-list">
            {reminders.slice(0, 6).map((rem) => (
              <div
                key={rem.event_id}
                className={`alert-pill ${rem.urgency === "overdue" ? "overdue" : ""}`}
              >
                <span style={{ fontWeight: 600 }}>{rem.event_type}:</span>
                <span>{rem.title}</span>
                {rem.client_name && <span style={{ color: "#64748b" }}>({rem.client_name})</span>}
                <button
                  type="button"
                  className="alert-dismiss-btn"
                  title="Dismiss reminder"
                  onClick={() => handleDismissReminder(rem.event_id)}
                >
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Control Toolbar */}
      <div className="calendar-toolbar">
        {/* Navigation */}
        <div className="calendar-nav-group">
          <button type="button" className="calendar-secondary-btn" onClick={handleToday}>
            Today
          </button>
          <button type="button" className="nav-arrow-btn" onClick={handlePrev} title="Previous">
            <FaChevronLeft />
          </button>
          <button type="button" className="nav-arrow-btn" onClick={handleNext} title="Next">
            <FaChevronRight />
          </button>
          <span className="current-period-label">{formatPeriodLabel()}</span>
        </div>

        {/* Filters */}
        <div className="calendar-filter-group">
          <div className="search-input-wrapper">
            <FaSearch />
            <input
              type="text"
              placeholder="Search meetings, flights..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            className="calendar-select-filter"
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
          >
            <option value="">All Integrated Modules</option>
            <option value="calendar">Calendar Events Only</option>
            <option value="sales">Sales & Flight Bookings</option>
            <option value="tasks">Task Manager</option>
            <option value="inquiries">Client Inquiries</option>
            <option value="documents">Document Compliance</option>
            <option value="invoices">Invoices & Receipts</option>
          </select>

          <select
            className="calendar-select-filter"
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
          >
            <option value="">All Event Types</option>
            <option value="Meeting">Meetings</option>
            <option value="Follow-up">Follow-ups</option>
            <option value="Flight Booking">Flight Bookings</option>
            <option value="Task Deadline">Tasks</option>
            <option value="Document Expiry">Expiries</option>
            <option value="Payment Due">Payments</option>
          </select>

          {/* View Toggle */}
          <div className="calendar-view-toggle">
            <button
              type="button"
              className={`view-toggle-btn ${currentView === "month" ? "active" : ""}`}
              onClick={() => {
                setCurrentView("month");
                setSearchParams({});
              }}
            >
              Month
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${currentView === "week" ? "active" : ""}`}
              onClick={() => {
                setCurrentView("week");
                setSearchParams({});
              }}
            >
              Week
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${currentView === "agenda" ? "active" : ""}`}
              onClick={() => {
                setCurrentView("agenda");
                setSearchParams({});
              }}
            >
              Agenda
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "80px 20px" }}>
          <FaSyncAlt className="fa-spin" style={{ fontSize: 28, color: "#2563eb", marginBottom: 12 }} />
          <div style={{ color: "#64748b", fontSize: 14 }}>Loading calendar schedule...</div>
        </div>
      ) : currentView === "month" ? (
        <CalendarMonthView
          currentDate={currentDate}
          events={displayEvents}
          onSelectEvent={(evt) => setDetailModalEvent(evt)}
          onAddEventForDate={handleAddEventForDate}
        />
      ) : currentView === "week" ? (
        <CalendarWeekView
          currentDate={currentDate}
          events={displayEvents}
          onSelectEvent={(evt) => setDetailModalEvent(evt)}
          onAddEventForDate={handleAddEventForDate}
        />
      ) : (
        <CalendarAgendaView
          events={displayEvents}
          onSelectEvent={(evt) => setDetailModalEvent(evt)}
          onDismissReminder={handleDismissReminder}
        />
      )}

      {/* Create / Edit Modal */}
      <CalendarEventModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedEventForModal(null);
        }}
        initialDate={selectedInitialDate}
        eventToEdit={selectedEventForModal}
        onSaved={() => {
          fetchEvents();
          fetchStats();
          fetchReminders();
        }}
      />

      {/* Detail Modal */}
      <CalendarEventDetailModal
        isOpen={Boolean(detailModalEvent)}
        event={detailModalEvent}
        onClose={() => setDetailModalEvent(null)}
        onEdit={(evt) => {
          setSelectedEventForModal(evt);
          setIsCreateModalOpen(true);
        }}
        onDeleted={() => {
          fetchEvents();
          fetchStats();
          fetchReminders();
        }}
      />
    </div>
  );
};

export default CalendarPage;
